import { PrintablesBookRole } from 'teacher-ui-client/components/printables/roles'
import type {
  PrintablesLibraryBookEntry,
  PrintablesLibrarySetEntry,
} from 'teacher-ui-client/contentful-client/entry-types'
import Component from '@glimmer/component'
import type { ThemeKeys } from 'teacher-ui-client/utils/themes'
import { service } from '@ember/service'
import type { BookNote } from 'teacher-ui-client/services/contentful-client'
import type ContentfulClient from 'teacher-ui-client/services/contentful-client'
import { printablesForRole, printablesPerBook } from 'teacher-ui-client/book-library/resources/printables'
import { PDF_TYPES, type PDFType } from 'teacher-ui-client/components/printables/pdf-types'
import { t, type IntlService } from 'ember-intl'
import { ResourcesBookNote } from 'teacher-ui-client/components/book-library/resources/book-note'
import { ResourcesPrintablesPDF } from 'teacher-ui-client/components/book-library/resources/book-printables-pdf'
import type { State } from 'reactiveweb/function'
import { trackedFunction } from 'reactiveweb/function'
import FidgetLoadingIndicator from '@blakeelearning/fidget/components/fidget/loading-indicator'
import { NoResultsMessage } from 'teacher-ui-client/components/no-results-message'
import { ContentfulEdit } from 'teacher-ui-client/components/contentful/edit'

interface Args {
  bookCode: string
  theme?: ThemeKeys
}

interface Signature {
  Element: HTMLElement
  Args: Args
}

interface PrintableWithPdfType {
  pdfType: PDFType
  printable: Exclude<PrintablesLibraryBookEntry['fields'][PDFType], undefined>
}

export class BookLibraryResources extends Component<Signature> {
  @service intl!: IntlService

  @service contentfulClient!: ContentfulClient

  findBookResources: State<
    Promise<{
      bookCode: string
      bookNotes: BookNote[]
      printablesBookSet: PrintablesLibrarySetEntry
    }>
  > = trackedFunction(this, async () => {
    const { bookCode } = this.args
    const [bookNotes, printablesBookSet] = await Promise.all([
      this.contentfulClient.getBookNotesForBookCode(bookCode),
      this.contentfulClient.getBookSetForBookCode(bookCode),
    ])
    return { bookCode, bookNotes, printablesBookSet }
  })

  private printablesByPdfType(printablesBookEntry?: PrintablesLibraryBookEntry): PrintableWithPdfType[] {
    return PDF_TYPES.reduce<PrintableWithPdfType[]>((printables, pdfType) => {
      const printable = printablesBookEntry?.fields[pdfType]
      if (printable) {
        return [...printables, { pdfType, printable }]
      } else {
        return printables
      }
    }, [])
  }

  resourcesViewModel = ({
    bookCode,
    bookNotes,
    printablesBookSet,
  }: {
    bookCode: string
    bookNotes: BookNote[]
    printablesBookSet?: PrintablesLibrarySetEntry
  }) => {
    // convert contentful data models to view models
    const studentPrintablesBook = printablesForRole(PrintablesBookRole.Student, printablesBookSet)
    const teacherPrintablesBook = printablesForRole(PrintablesBookRole.Teacher, printablesBookSet)
    const studentPrintables = this.printablesByPdfType(studentPrintablesBook)
    const teacherPrintables = this.printablesByPdfType(teacherPrintablesBook)

    // count the number of resources
    const studentResourcesCount = printablesPerBook(studentPrintablesBook)
    const teacherResourcesCount = printablesPerBook(teacherPrintablesBook) + bookNotes.length
    const count = studentResourcesCount + teacherResourcesCount

    return {
      count,
      byRole: [
        {
          title: this.intl.t('printables.student'),
          role: PrintablesBookRole.Student,
          printables: studentPrintables,
          bookCode,
          count: studentResourcesCount,
          printablesBookEntryId: studentPrintablesBook?.sys.id,
        },
        {
          title: this.intl.t('printables.teacher'),
          role: PrintablesBookRole.Teacher,
          printables: teacherPrintables,
          bookCode,
          bookNotes,
          count: teacherResourcesCount,
          printablesBookEntryId: teacherPrintablesBook?.sys.id,
        },
      ],
    }
  }

  <template>
    <section ...attributes>
      {{#if this.findBookResources.isPending}}
        <FidgetLoadingIndicator
          data-test-book-resources-loading
          @show={{true}}
          @centered={{true}}
          @appearance="small"
          @overlay={{false}}
        />
      {{else if this.findBookResources.value}}
        {{#let (this.resourcesViewModel this.findBookResources.value) as |resources|}}
          {{#if resources.count}}
            {{#each resources.byRole as |resourcesForRole|}}
              {{#if resourcesForRole.count}}
                <div class="mb-2">
                  <div class="text-dusty-black-250 mb-1 flex items-center space-x-2 font-semibold uppercase">
                    <span>{{resourcesForRole.title}}</span>
                    <ContentfulEdit
                      data-test-contentful-edit-link
                      data-test-resource-role={{resourcesForRole.role}}
                      @entryId={{resourcesForRole.printablesBookEntryId}}
                    />
                  </div>
                  <div class="flex flex-wrap items-stretch gap-5 pb-2">
                    {{#each resourcesForRole.printables as |printableItem|}}
                      <ResourcesPrintablesPDF
                        data-test-book-printables-pdf
                        @pdfType={{printableItem.pdfType}}
                        @printable={{printableItem.printable}}
                        @bookCode={{resourcesForRole.bookCode}}
                        @role={{resourcesForRole.role}}
                        @themeKey={{@theme}}
                      />
                    {{/each}}
                    {{#each resourcesForRole.bookNotes as |bookNote|}}
                      <ResourcesBookNote
                        data-test-book-note-pdf
                        @bookNote={{bookNote}}
                        @bookCode={{resourcesForRole.bookCode}}
                        @role={{resourcesForRole.role}}
                        @themeKey={{@theme}}
                      />
                    {{/each}}
                  </div>
                </div>
              {{/if}}
            {{/each}}
          {{else}}
            <NoResultsMessage @compact={{true}} @body={{t "bookLibrary.components.bookModal.noResources"}} />
          {{/if}}
        {{/let}}
      {{/if}}
    </section>
  </template>
}

export default BookLibraryResources
